import { Component, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import {
  CapitalizePipe,
  ClickOutsideDirective,
  DetailsMenuItem,
  MenuDropdownComponent,
  RcNavigateDirective,
  StoredEntityService,
  User,
} from 'shared';
import { NavigationStart, Router, RouterLink, RouterLinkActive, UrlTree } from '@angular/router';
import {
  PopoverAnchorDirective,
  PopoverBodyTemplateDirective,
  PopoverComponent,
  TooltipDirective,
} from '@progress/kendo-angular-tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { AuthService } from '@auth0/auth0-angular';
import { logout } from '@crowndesk-core/session/logout';
import { ImpersonationService } from '@crowndesk-core/login-as/impersonation.service';

@Component({
  selector: 'rc-crowndesk-menu',
  imports: [
    CapitalizePipe,
    RouterLink,
    RouterLinkActive,
    TooltipDirective,
    TranslateModule,
    MenuDropdownComponent,
    NgClass,
    PopoverAnchorDirective,
    ButtonComponent,
    PopoverComponent,
    PopoverBodyTemplateDirective,
    ClickOutsideDirective,
    RcNavigateDirective,
    NgTemplateOutlet,
  ],
  templateUrl: './crowndesk-menu.component.html',
  styleUrl: './crowndesk-menu.component.scss',
  host: { '[class.collapsed]': 'collapsed() && !isMobile()' },
})
export class CrowndeskMenuComponent {
  mobileMenuAnchor = viewChild<PopoverAnchorDirective>('userMenuMobile');
  userMenuAnchor = viewChild<PopoverAnchorDirective>('userMenuAnchor');
  mobileUserMenuAnchor = viewChild<PopoverAnchorDirective>('mobileUserMenu');
  isMobile = input<boolean>(false);
  menu = input.required<DetailsMenuItem[]>();
  neverDisabled = input<boolean>(false);
  user = input.required<User>();
  authService = inject(AuthService);
  #router = inject(Router);
  setActiveRoute = output<string>();
  #storedEntityService = inject(StoredEntityService);
  #impersonationService = inject(ImpersonationService);

  /** A Royal Crown employee is logged in as a client employee: logging out then only stops that. */
  protected readonly isImpersonating = computed(() => !!this.#impersonationService.clientEmployeeId());

  /** Only the icons are shown; hovering the menu shows it in full over the page. Remembered in this browser. */
  readonly collapsed = signal(readCollapsed());
  /** Keeps a collapsed menu shown in full while the user menu is open, which lies outside the menu. */
  readonly userMenuOpen = signal(false);

  hasEntity = false;
  opened = false;
  userMenuItem = computed<DetailsMenuItem>(() => {
    return {
      text: this.user().name,
      subText: this.user().companyName,
      items: [
        {
          text: 'LOGOUT',
          action: this.logout,
        },
      ],
    };
  });

  constructor() {
    effect(() => {
      try {
        localStorage.setItem(COLLAPSED_KEY, String(this.collapsed()));
      } catch {
        // storage can be full or blocked: the choice then only lasts while the page is open
      }
    });

    this.#storedEntityService.getStoredEntity().subscribe((val) => {
      this.hasEntity = !!val;
    });

    // force re-render of the anchor items, because known routerLinkActive issue (nested router-links)
    this.#router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      // this.opened = false;
      // setTimeout(() => {
      //   this.opened = true;
      // }, 0);
    });
  }

  isDropdownActive(item: DetailsMenuItem): boolean {
    const currentUrl = this.#router.url.split('?')[0].split('#')[0];
    // The page must lie under the child's own path: `/tools/carts/12/articles` contains "articles" but is a tool
    return !!item.items?.some((child) => {
      // An item url starting with a slash is absolute (the dropdown links it without the dropdown's url)
      const path = child.url?.startsWith('/') ? child.url : `/${item.url}/${child.url}`;
      return currentUrl === path || currentUrl.startsWith(`${path}/`);
    });
  }

  /** While impersonating, logging out only ends the impersonation and returns to the client employee selection. */
  logout(): void {
    if (this.#impersonationService.clientEmployeeId()) {
      this.closeUserMenus();
      this.#impersonationService.stop();
      return;
    }
    logout(this.authService);
  }

  /** Kendo aligns the popover before its body is rendered (size 0), so realign once it has its real size. */
  repositionUserMenu(): void {
    requestAnimationFrame(() => this.userMenuAnchor()?.popupRef?.popup.instance.onResize());
  }

  /** The user menu of the side menu (desktop) or of the user icon (mobile). */
  closeUserMenus(): void {
    // hiding it from code does not emit the popover's hide event
    this.userMenuOpen.set(false);
    this.userMenuAnchor()?.hide();
    this.mobileUserMenuAnchor()?.hide();
  }

  closeMobileMenu(): void {
    this.mobileMenuAnchor()?.hide();
  }

  toggleCollapsed(): void {
    this.collapsed.update((collapsed) => !collapsed);
  }

  setActive(name: string) {
    this.setActiveRoute.emit(name);
  }

  isActiveLink(url: string): boolean {
    const tree: UrlTree = this.#router.createUrlTree([url]);
    return this.#router.isActive(tree, {
      paths: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
      queryParams: 'ignored',
    });
  }
}

const COLLAPSED_KEY = 'crowndesk-menu-collapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
}
